import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Eye, EyeSlash, CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";

const LOGO_SB_WHITE = "/logo-surbakti-white.png";

/**
 * ResetPasswordPage — landing page opened from the "Reset Password" email
 * or WhatsApp link. Flow:
 *   1. Parse `?token=` from the URL, hit `/api/auth/reset-password/verify`
 *      to confirm it's valid + unexpired.
 *   2. If valid: render new-password form.
 *   3. On submit: POST `/api/auth/reset-password` with { token, new_password }.
 *      Success → toast + redirect to /login.
 *
 * Access is public (no auth guard) — the token IS the credential.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [tokenEmail, setTokenEmail] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTokenError("Link reset tidak lengkap. Silakan minta ulang dari halaman login.");
      setChecking(false);
      return () => {};
    }
    api.get("/auth/reset-password/verify", { params: { token } })
      .then(({ data }) => { if (!cancelled) setTokenEmail(data.email || ""); })
      .catch((err) => { if (!cancelled) setTokenError(formatApiError(err)); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (password !== confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password berhasil diubah");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #fb923c 0%, #ea580c 45%, #9a3412 100%)",
      }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-primary rounded-full">
            <img src={LOGO_SB_WHITE} alt="Logo Sari Bakti Jaya" className="w-9 h-9 object-contain" />
          </div>
          <div>
            <div className="font-black text-2xl leading-none tracking-tight">epool</div>
            <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-muted-foreground mt-1.5">
              Reset Password
            </div>
          </div>
        </div>

        {checking && (
          <div className="text-center py-12 text-muted-foreground text-sm" data-testid="reset-checking">
            Memverifikasi link…
          </div>
        )}

        {!checking && tokenError && (
          <div className="text-center space-y-4 py-4" data-testid="reset-invalid">
            <Warning size={48} weight="fill" className="text-rose-600 mx-auto" />
            <div className="font-bold text-lg">Link Tidak Valid</div>
            <p className="text-sm text-muted-foreground">{tokenError}</p>
            <Link to="/login">
              <Button className="w-full h-11 font-bold uppercase tracking-widest" data-testid="reset-back-to-login">
                Kembali ke Login
              </Button>
            </Link>
          </div>
        )}

        {!checking && !tokenError && done && (
          <div className="text-center space-y-4 py-4" data-testid="reset-done">
            <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto" />
            <div className="font-bold text-lg">Password Diubah</div>
            <p className="text-sm text-muted-foreground">Mengalihkan ke halaman login…</p>
          </div>
        )}

        {!checking && !tokenError && !done && (
          <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
            <p className="text-sm text-muted-foreground">
              Buat password baru untuk akun <span className="font-mono font-bold text-foreground">{tokenEmail}</span>.
            </p>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                Password Baru
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  type={showPassword ? "text" : "password"}
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="h-12 pl-10 pr-10 rounded-lg"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="reset-password-toggle"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                Ketik Ulang Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ulangi password baru"
                  className="h-12 pl-10 rounded-lg"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg h-12 font-bold uppercase tracking-widest text-base"
              data-testid="reset-submit-button"
            >
              {submitting ? "Menyimpan..." : "Simpan Password Baru"}
              <ArrowRight className="ml-2" size={16} weight="bold" />
            </Button>
            <div className="text-center pt-2">
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="reset-cancel-link">
                Batal, kembali ke Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
